import { useCallback, useState } from 'react';

import { ChevronDownIcon } from '@/react/components/icons/ChevronDownIcon';
import { EmptyBoxIcon } from '@/react/components/icons/EmptyBoxIcon';
import { PlusIcon } from '@/react/components/icons/PlusIcon';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Counter } from '@/components/ui/counter';
import { StatusBadge, STATUS_BADGE_COMPACT } from '@/components/ui/status-badge';
import { cn } from '@/lib/utils';

import { ConfirmationDialog } from '../common/ConfirmationDialog';
import { OrderDocumentsGenerateBar, type OrderDocumentsGenerateBarMode } from './OrderDocumentsGenerateBar';
import { openActivityCentrePanel } from '@components/ActivityCentre/useActivityCentrePanel';
import { EventActionsDropdown } from './EventActionsDropdown';
import { SpineCancellationNotice } from './SpineCancellationNotice';
import { spineCancellationTargets } from './spine-cancellation';
import { EventCompactCard, EventDetailMobileSheet } from './EventDetailMobileCard';
import {
  EventTypeMeta,
  EventServingAndTimings,
  EventAddressAndContacts,
  EventDescriptionAndInstructions,
  EventYieldBar,
  SpineOrderCard,
  EventMenuItems,
} from './EventDetailFields';

import { useTranslation } from '@hooks/useTranslation';
import { useOrderEvents, useDeleteEvent, useGenerateEventOrders } from '@hooks/events';
import { buildEventEditPath } from '@/react/utils/eventEditNavigation';
import { eventHistoryPath } from '@/react/utils/historyPaths';
import { can, CAPABILITIES, type CurrentUser } from '~/react/lib/capabilities';

import type { ApiEvent } from '@api/events';
import { PRODUCT_LINE_LABELS } from '@components/common/ProductLinePicker/productLines';
import { createEventStatusBadgeConfig, resolveEventStatusKey } from './event-info-config';
import { EventsFilterTabs, type EventFilter } from './EventsFilterTabs';

import { CARD_RADIUS, toSentenceCase } from './event-card-shared';

const CARD_PADDING = 'p-4 md:p-6';

interface EventsSectionCardProps {
  orderId: number;
  currentUser?: CurrentUser;
  onAddEvents?: () => void;
  actionsDisabled?: boolean;
  collapsedEventIds?: number[];
}

export function EventsSectionCard({ orderId, currentUser, onAddEvents, actionsDisabled, collapsedEventIds }: EventsSectionCardProps) {
  const { t } = useTranslation();
  const { data: events = [] } = useOrderEvents(orderId);
  const canWrite = can(currentUser, CAPABILITIES.EVENTS_WRITE);
  // Printing EOs is its own capability — fulfilment roles print without edit rights.
  const canGenerate = can(currentUser, CAPABILITIES.EVENT_ORDERS_GENERATE);

  const generateEventOrders = useGenerateEventOrders();
  const handleGenerateForEvent = useCallback(
    (eventId: number, documentTypes: string[]) => {
      generateEventOrders.mutate({
        all_events_on_date: false,
        event_ids: [eventId],
        document_types: documentTypes,
      });
    },
    [generateEventOrders.mutate],
  );
  const generateBarMode: OrderDocumentsGenerateBarMode | null =
    generateEventOrders.isPending ? 'loading'
    : generateEventOrders.isSuccess ? 'queued'
    : generateEventOrders.isError ? 'error'
    : null;
  const resetGenerateBar = () => generateEventOrders.reset();

  const [eventFilter, setEventFilter] = useState<EventFilter>('all');
  const visibleEvents =
    eventFilter === 'all'
      ? events
      : events.filter((event) => resolveEventStatusKey(event.event_status) === eventFilter);

  return (
    <>
      <OrderDocumentsGenerateBar
        mode={generateBarMode}
        onDismiss={resetGenerateBar}
        onAutoDismiss={resetGenerateBar}
        onQueuedLinkClick={() => openActivityCentrePanel('processing')}
        copy={{
          loading: t('download_modal.generate_loading'),
          queuedRest: t('download_modal.generate_success_rest'),
          error: t('download_modal.generate_error'),
        }}
      />
      <div className="space-y-0 md:space-y-4">
      <div className="sticky top-0 z-20 -mx-6 space-y-2 bg-off-white px-6 pt-2 pb-4 before:pointer-events-none before:absolute before:inset-x-0 before:bottom-full before:h-8 before:bg-off-white md:static md:mx-0 md:space-y-0 md:bg-transparent md:px-0 md:py-0 md:before:hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <h3 className="font-kapra-expanded text-2xl font-black uppercase leading-normal text-off-black">
              {t('events_section.title')}
            </h3>
            <Counter value={events.length} />
          </div>
          {canWrite && (
            <Button variant="tertiary" onClick={onAddEvents} disabled={actionsDisabled} className="hidden md:inline-flex">
              <PlusIcon className="size-4" />
              {t('events_section.add_events')}
            </Button>
          )}
        </div>
        {events.length > 0 && (
          <EventsFilterTabs
            events={events}
            active={eventFilter}
            onChange={setEventFilter}
            className="md:hidden [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
          />
        )}
      </div>

      {events.length > 0 ? (
        <div className="space-y-3">
          {visibleEvents.map((event) => (
            <EventDetailCard
              key={event.id}
              event={event}
              orderId={orderId}
              canWrite={canWrite}
              canGenerate={canGenerate}
              onGenerateEvent={handleGenerateForEvent}
              actionsDisabled={actionsDisabled}
              defaultCollapsed={collapsedEventIds?.includes(event.id) ?? false}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-6 py-36 rounded-input border border-dinnerplate-white bg-off-white">
          <EmptyBoxIcon />
          <p className="font-inter text-sm font-normal leading-tight text-grey antialiased">
            {t('events_section.empty_state')}
          </p>
        </div>
      )}
      </div>
    </>
  );
}

function EventDetailCard({ event, orderId, canWrite, canGenerate, onGenerateEvent, actionsDisabled, defaultCollapsed }: { event: ApiEvent; orderId: number; canWrite: boolean; canGenerate: boolean; onGenerateEvent: (eventId: number, documentTypes: string[]) => void; actionsDisabled?: boolean; defaultCollapsed?: boolean }) {
  const [expanded, setExpanded] = useState(!defaultCollapsed);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const { t } = useTranslation();
  const toggleExpanded = useCallback(() => setExpanded((prev) => !prev), []);
  const deleteEventMutation = useDeleteEvent();
  const handleDeleteConfirm = useCallback(() => {
    deleteEventMutation.mutate(
      { orderId, eventId: event.id },
      { onSuccess: () => setDeleteDialogOpen(false) },
    );
  }, [deleteEventMutation.mutate, orderId, event.id]);
  const editEventPath = buildEventEditPath(orderId, event.id);

  const titleLabel = event.product_line
    ? (PRODUCT_LINE_LABELS[event.product_line] ?? toSentenceCase(event.product_line))
    : t('events_section.event_label');

  const statusKey = resolveEventStatusKey(event.event_status);
  const statusBadgeConfig = createEventStatusBadgeConfig({
    draft: t('events_section.status.draft'),
    published: t('events_section.status.published'),
    cancelled: t('events_section.status.cancelled'),
  });

  return (
    <>
      <div data-testid="event-detail-card-desktop" className="hidden md:block">
        <div className={cn(CARD_RADIUS, 'border border-dinnerplate-white shadow-event-card', CARD_PADDING)}>
          <div className="sticky top-12 z-10 -mx-4 -mt-4 rounded-t-input bg-off-white px-4 pt-4 pb-3 flex items-center justify-between md:static md:mx-0 md:mt-0 md:rounded-none md:bg-transparent md:p-0">
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="rounded-xs border-off-black px-2 py-1 font-inter text-xs font-normal leading-tight text-off-black">
                {event.event_id}
              </Badge>
              <span className="font-inter text-lg font-semibold leading-normal text-off-black">
                {titleLabel}
              </span>
              <StatusBadge value={statusKey} config={statusBadgeConfig} className={STATUS_BADGE_COMPACT} />
              <Button
                type="button"
                variant="ghost"
                onClick={toggleExpanded}
                aria-label={`${expanded ? t('events_section.collapse_event') : t('events_section.expand_event')} ${event.event_id}`.trim()}
                className="p-0 hover:bg-transparent"
              >
                <ChevronDownIcon
                  aria-hidden="true"
                  className={cn(
                    'text-off-black chevron-motion',
                    expanded && 'rotate-180'
                  )}
                />
              </Button>
            </div>
            <div className="hidden md:block">
              <EventActionsDropdown
                editEventPath={editEventPath}
                historyPath={eventHistoryPath(orderId, event.id)}
                canWrite={canWrite}
                canGenerate={canGenerate}
                eventTypeKey={event.event_type}
                productLine={event.product_line}
                eventStatus={event.event_status}
                onGenerate={(documentTypes) => onGenerateEvent(event.id, documentTypes)}
                disabled={actionsDisabled}
                onDelete={() => setDeleteDialogOpen(true)}
              />
            </div>
          </div>

          {expanded ? (
            <div className="mt-3 flex flex-col gap-6">
              <EventTypeMeta event={event} />
              <EventServingAndTimings event={event} />
              <EventAddressAndContacts event={event} />
              <EventDescriptionAndInstructions event={event} />
              {event.spine_order ? (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr]">
                  <EventYieldBar />
                  <SpineOrderCard event={event} />
                </div>
              ) : (
                <EventYieldBar />
              )}
              <EventMenuItems event={event} />
            </div>
          ) : null}
        </div>
      </div>

      <div data-testid="event-detail-card-mobile" className="md:hidden">
        <EventCompactCard
          event={event}
          titleLabel={titleLabel}
          statusKey={statusKey}
          statusBadgeConfig={statusBadgeConfig}
          onOpen={() => setMobileSheetOpen(true)}
        />
        <EventDetailMobileSheet
          event={event}
          titleLabel={titleLabel}
          statusKey={statusKey}
          statusBadgeConfig={statusBadgeConfig}
          open={mobileSheetOpen}
          onOpenChange={setMobileSheetOpen}
          editEventPath={canWrite && !actionsDisabled ? editEventPath : undefined}
        />
      </div>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        title={t('events_section.delete_dialog.title')}
        message={t('events_section.delete_dialog.message')}
        confirmLabel={t('events_section.delete_dialog.confirm')}
        cancelLabel={t('events_section.delete_dialog.cancel')}
        confirmingLabel={t('events_section.delete_dialog.loading')}
        isPending={deleteEventMutation.isPending}
        note={<SpineCancellationNotice targets={spineCancellationTargets([event])} />}
      />
    </>
  );
}
